import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y } from 'swiper/modules';
import css from './GoldenScreenGallery.module.css';
import { ListingCardAnimate } from '../../../components/GsapAnimation/ListingCardAnimate/ListingCardAnimate';
import shutterstockLogo from '../../../assets/shutterstock-logo.png';
import { delay } from 'lodash';
import { duration } from 'moment';

import { optimizeCloudinaryUrl, getCloudinarySrcSet } from '../../../util/cloudinaryHelpers';

const GOLDEN_GALLERY = {
  maxContentWidth: 1260,
  cardWidth: 400,
  cardHeight: 533,
  columnGap: 30,
  columnOffsets: [0, 340, 710],
  columnGaps: [929, 240, 240],
};

const CAROUSEL_MAX_WIDTH = 1024;
const DESKTOP_VIEWPORT_MIN_WIDTH = CAROUSEL_MAX_WIDTH + 1;

/**
 * Computes layout data for a three-column flex-based staggered gallery.
 */
const computeGoldenGalleryLayout = (images, containerWidth) => {
  const width = Math.min(containerWidth, GOLDEN_GALLERY.maxContentWidth);
  const scale = width / GOLDEN_GALLERY.maxContentWidth;
  const cardWidth = GOLDEN_GALLERY.cardWidth * scale;
  const cardHeight = GOLDEN_GALLERY.cardHeight * scale;
  const columnGap = GOLDEN_GALLERY.columnGap * scale;
  const columnOffsets = GOLDEN_GALLERY.columnOffsets.map(offset => offset * scale);
  const columnGaps = GOLDEN_GALLERY.columnGaps.map(gap => gap * scale);

  const columns = Array.from({ length: 3 }, () => []);

  images.forEach((image, idx) => {
    const colIdx = idx % 3;
    columns[colIdx].push({ image, idx });
  });

  let containerHeight = 0;

  const columnData = columns.map((colItems, colIdx) => {
    let currentTop = columnOffsets[colIdx];
    const items = colItems.map(item => {
      const top = currentTop;
      currentTop += cardHeight + (colIdx < columnGaps.length ? columnGaps[colIdx] : 0);
      return { ...item, top };
    });

    const colHeight = items.length ? items[items.length - 1].top + cardHeight : 0;

    containerHeight = Math.max(containerHeight, colHeight);

    return { items, colHeight };
  });

  return {
    columns: columnData,
    cardWidth,
    cardHeight,
    columnGap,
    containerHeight,
  };
};

/**
 * Staggered three-column gallery for the Golden Screen listing experience.
 */
const GoldenScreenGallery = props => {
  const { images, className, ariaLabel } = props;
  const desktopGridRef = useRef(null);
  const swiperRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const [isCarouselLayout, setIsCarouselLayout] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${CAROUSEL_MAX_WIDTH}px)`);
    const updateLayoutMode = event => {
      setIsCarouselLayout(event.matches);
    };

    setIsCarouselLayout(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateLayoutMode);
    } else {
      mediaQuery.addListener(updateLayoutMode);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateLayoutMode);
      } else {
        mediaQuery.removeListener(updateLayoutMode);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const gridNode = desktopGridRef.current;
    if (!gridNode || !images?.length || isCarouselLayout) {
      setLayout(null);
      return undefined;
    }

    const updateLayout = () => {
      if (window.innerWidth < DESKTOP_VIEWPORT_MIN_WIDTH) {
        setLayout(null);
        return;
      }

      setLayout(computeGoldenGalleryLayout(images, gridNode.offsetWidth));
    };

    updateLayout();

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateLayout) : null;

    if (resizeObserver) {
      resizeObserver.observe(gridNode);
    }

    window.addEventListener('resize', updateLayout);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateLayout);
    };
  }, [images, isCarouselLayout]);

  useEffect(() => {
    if (!isCarouselLayout || !images?.length) {
      setActiveIndex(0);
      return undefined;
    }

    setActiveIndex(0);
    swiperRef.current?.slideTo(0, 0, false);
    return undefined;
  }, [images, isCarouselLayout]);

  // Per-card subtle tilt/follow on hover — only the hovered card moves
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const grid = desktopGridRef.current;
    if (!grid || !layout) return undefined;

    const STRENGTH = 12; // max px offset at card edge
    const LERP = 0.12;   // smoothness — lower = more lag, higher = snappier

    // State per card
    const cards = Array.from(grid.querySelectorAll('article'));
    const state = new Map();

    const cleanup = [];

    cards.forEach(card => {
      const s = { tx: 0, ty: 0, cx: 0, cy: 0, raf: null, hovering: false };
      state.set(card, s);

      const tick = () => {
        s.cx += (s.tx - s.cx) * LERP;
        s.cy += (s.ty - s.cy) * LERP;
        card.style.transform = `translate(${s.cx}px, ${s.cy}px)`;

        const settled =
          Math.abs(s.tx - s.cx) < 0.05 &&
          Math.abs(s.ty - s.cy) < 0.05;

        if (!settled || s.hovering) {
          s.raf = requestAnimationFrame(tick);
        } else {
          s.raf = null;
          card.style.transform = '';
        }
      };

      const start = () => { if (!s.raf) s.raf = requestAnimationFrame(tick); };

      const onMouseMove = e => {
        const r = card.getBoundingClientRect();
        // Offset from card centre, normalised -0.5 to +0.5
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        s.tx = nx * STRENGTH;
        s.ty = ny * STRENGTH;
        start();
      };

      const onMouseEnter = () => { s.hovering = true; start(); };

      const onMouseLeave = () => {
        s.hovering = false;
        s.tx = 0;
        s.ty = 0;
        start();
      };

      card.addEventListener('mousemove', onMouseMove);
      card.addEventListener('mouseenter', onMouseEnter);
      card.addEventListener('mouseleave', onMouseLeave);

      cleanup.push(() => {
        cancelAnimationFrame(s.raf);
        card.removeEventListener('mousemove', onMouseMove);
        card.removeEventListener('mouseenter', onMouseEnter);
        card.removeEventListener('mouseleave', onMouseLeave);
        card.style.transform = '';
      });
    });

    return () => cleanup.forEach(fn => fn());
  }, [layout, isCarouselLayout]);

  if (!images?.length) {
    return null;
  }

  const handleSwiper = swiper => {
    swiperRef.current = swiper;
    setActiveIndex(swiper.activeIndex || 0);
  };

  const handleSlideChange = swiper => {
    setActiveIndex(swiper.activeIndex || 0);
  };

  const handlePaginationClick = index => {
    swiperRef.current?.slideTo(index);
    setActiveIndex(index);
  };

  const renderCard = ({ image, idx, carousel = false, style }) => (
    <ListingCardAnimate
      key={image.id || idx}
      options={{ index: idx, y: 60, delay: 0.8, duration: 0.8 }}
    >
      <article
        className={classNames(css.card, {
          [css.cardCarousel]: carousel,
        })}
        style={style}
      >
        <img
          className={css.cardImage}
          src={optimizeCloudinaryUrl(image.url, 800)}
          srcSet={getCloudinarySrcSet(image.url)}
          sizes="(max-width: 1024px) 100vw, 400px"
          alt={image.title || `Experience image ${idx + 1}`}
          loading="lazy"
          decoding="async"
          width="400"
          height="533"
        />
        <div className={css.cardShade} aria-hidden="true" />

        {image.isTimeStampNeeded && image.timeStampText ? (
          <h3 className={css.cardTitle}>{image.timeStampText}</h3>
        ) : null}
      </article>
    </ListingCardAnimate>
  );

  return (
    <section className={classNames(css.root, className)} aria-label={ariaLabel}>
      {isCarouselLayout ? (
        <div className={css.carouselWrapper}>
          <Swiper
            modules={[A11y]}
            onSwiper={handleSwiper}
            onSlideChange={handleSlideChange}
            loop={false}
            slidesPerView={1}
            spaceBetween={0}
            className={css.swiper}
            centeredSlides={false}
            initialSlide={0}
            breakpoints={{
              0: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              480: {
                slidesPerView: 1.4,
                spaceBetween: 20,
              },
              620: {
                slidesPerView: 1.8,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 2.6,
                spaceBetween: 10,
              },
            }}
          >
            {images.map((image, idx) => (
              <SwiperSlide key={image.id || idx} className={css.slide}>
                {renderCard({
                  image,
                  idx,
                  carousel: true,
                })}
              </SwiperSlide>
            ))}
          </Swiper>

          {images.length > 1 ? (
            <div className={css.pagination} aria-label="Gallery pagination">
              {images.map((image, idx) => (
                <button
                  key={`${image.id || idx}-pagination`}
                  type="button"
                  className={classNames(css.paginationButton, {
                    [css.paginationButtonActive]: idx === activeIndex,
                  })}
                  aria-label={`Go to image ${idx + 1}`}
                  aria-current={idx === activeIndex ? 'true' : undefined}
                  onClick={() => handlePaginationClick(idx)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div
          ref={desktopGridRef}
          className={css.gridDesktop}
          style={layout ? { height: `${layout.containerHeight}px` } : undefined}
        >
          {layout?.columns?.map((column, colIdx) => (
            <div
              key={colIdx}
              className={css.column}
              style={{
                marginTop: `${column.items[0]?.top || 0}px`,
                gap: `${GOLDEN_GALLERY.columnGaps[colIdx] *
                  (layout.cardWidth / GOLDEN_GALLERY.cardWidth)}px`,
              }}
            >
              {column.items.map(({ image, idx }) =>
                renderCard({
                  image,
                  idx,
                })
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default GoldenScreenGallery;
